import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, RotateCcw, Package, Home } from "lucide-react";

const MonkeyBananaExplorer = () => {
  const [gameState, setGameState] = useState({
    position: 0, // 猴子当前位置（米）
    carriedBananas: 0, // 猴子背负的香蕉数
    isMoving: false, // 是否正在移动
    isGameOver: false, // 游戏是否结束
    gameOverReason: "", // 游戏结束原因
    finalScore: 0, // 最终到家的香蕉数
  });

  // 地面香蕉储存，key为位置，value为香蕉数
  const [groundBananas, setGroundBananas] = useState({ 0: 300 });
  const [history, setHistory] = useState([]);
  const [moveDistance, setMoveDistance] = useState("");
  const [targetCarriedBananas, setTargetCarriedBananas] = useState("");
  const [dropAmount, setDropAmount] = useState("");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // 显示选项控制
  const [displayOptions, setDisplayOptions] = useState({
    showStationPositions: true,     // 显示基站位置标注
    showPositionLine: true,         // 显示猴子位置辅助线
    showTrajectorySteps: true,      // 显示轨迹步数信息
    showRemainingBananas: true,     // 显示剩余香蕉信息
    showBananaOperations: true,     // 显示香蕉操作记录
    showDistanceMarkers: false,     // 显示距离标记（默认关闭，突出香蕉信息）
  });

  // 功能控制
  const [featureOptions, setFeatureOptions] = useState({
    enableDropBananas: false,       // 启用放下香蕉功能
  });

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 当携带香蕉数变化时，调整放下数量
  useEffect(() => {
    const dropAmountNum = parseInt(dropAmount) || 0;
    if (dropAmountNum > gameState.carriedBananas) {
      setDropAmount("");
    }
  }, [gameState.carriedBananas, dropAmount]);

  // 添加到历史记录
  const addToHistory = (action) => {
    const newStep = {
      id: Date.now(),
      type: action.type, // 'move', 'pickup', 'drop'
      position: gameState.position,
      ...action,
    };
    setHistory((prev) => [...prev, newStep]);
  };

  // 移动猴子
  const moveMonkey = (direction) => {
    if (gameState.isMoving) return;

    const distance = Math.abs(parseInt(moveDistance) || 10);
    const newPosition =
      direction === "right"
        ? Math.min(gameState.position + distance, 100)
        : Math.max(gameState.position - distance, 0);

    const actualDistance = Math.abs(newPosition - gameState.position);

    if (actualDistance === 0) return;

    // 检查是否有足够香蕉移动
    if (gameState.carriedBananas < actualDistance) {
      setGameState((prev) => ({
        ...prev,
        isGameOver: true,
        gameOverReason: `🍌💔 猴子陷入困境！香蕉不够走${actualDistance}米了，只剩${gameState.carriedBananas}根香蕉。饿着肚子寸步难行，请调整策略再试！`,
      }));
      return;
    }

    setGameState((prev) => ({ ...prev, isMoving: true }));

    // 模拟移动过程
    let currentPos = gameState.position;
    let remainingBananas = gameState.carriedBananas;
    const step = newPosition > currentPos ? 1 : -1;

    const animate = () => {
      if (Math.abs(currentPos - newPosition) > 0) {
        currentPos += step;
        remainingBananas--;

        setGameState((prev) => ({
          ...prev,
          position: currentPos,
          carriedBananas: remainingBananas,
        }));

        setTimeout(animate, 100);
      } else {
        setGameState((prev) => ({ ...prev, isMoving: false }));

        // 检查是否到家
        if (newPosition === 100) {
          setGameState((prev) => ({
            ...prev,
            finalScore: remainingBananas,
            gameOverReason: `🏠🎉 恭喜！猴子成功到家了！带回了${remainingBananas}根香蕉！`,
          }));
        }

        addToHistory({
          type: 'move',
          fromPosition: gameState.position,
          toPosition: newPosition,
          bananasUsed: actualDistance,
          carriedBananas: remainingBananas,
          distance: actualDistance,
        });
      }
    };

    setTimeout(animate, 100);
  };

  // 拿起香蕉
  const pickupBananas = () => {
    if (gameState.isMoving) return;

    const targetNum = parseInt(targetCarriedBananas) || 50;
    const currentGroundBananas = groundBananas[gameState.position] || 0;
    const maxCanCarry = 100 - gameState.carriedBananas;
    const wantToCarry = Math.min(
      targetNum - gameState.carriedBananas,
      maxCanCarry,
      currentGroundBananas
    );

    if (wantToCarry <= 0) {
      if (gameState.carriedBananas >= 100) {
        setGameState((prev) => ({
          ...prev,
          isGameOver: true,
          gameOverReason: `🐵💥 猴子已经背满100根香蕉了！再多一根都会被压倒在地！先放下一些再说吧...`,
        }));
      }
      return;
    }

    if (gameState.carriedBananas + wantToCarry > 100) {
      setGameState((prev) => ({
        ...prev,
        isGameOver: true,
        gameOverReason: `🐵💥 哎呀！想背${
          gameState.carriedBananas + wantToCarry
        }根香蕉太重了！猴子不堪生活重负，被压倒在地了！香蕉散落一地，场面十分狼狈... 最多只能背100根哦！`,
      }));
      return;
    }

    // 记录操作前状态
    const beforeCarried = gameState.carriedBananas;
    const beforeGround = currentGroundBananas;

    // 如果操作成功，清除异常状态
    setGameState((prev) => ({
      ...prev,
      carriedBananas: prev.carriedBananas + wantToCarry,
      isGameOver: false,
      gameOverReason: "",
    }));
    setGroundBananas((prev) => ({
      ...prev,
      [gameState.position]: currentGroundBananas - wantToCarry,
    }));

    // 记录香蕉操作
    addToHistory({
      type: 'pickup',
      bananaAmount: wantToCarry,
      beforeCarried,
      afterCarried: beforeCarried + wantToCarry,
      beforeGround,
      afterGround: beforeGround - wantToCarry,
    });
  };

  // 放下香蕉
  const dropBananas = (amount) => {
    if (gameState.isMoving) return;

    const actualDrop = Math.min(amount, gameState.carriedBananas);
    if (actualDrop <= 0) return;

    // 记录操作前状态
    const beforeCarried = gameState.carriedBananas;
    const beforeGround = groundBananas[gameState.position] || 0;

    // 如果操作成功，清除异常状态
    setGameState((prev) => ({
      ...prev,
      carriedBananas: prev.carriedBananas - actualDrop,
      isGameOver: false,
      gameOverReason: "",
    }));
    setGroundBananas((prev) => ({
      ...prev,
      [gameState.position]: (prev[gameState.position] || 0) + actualDrop,
    }));

    // 记录香蕉操作
    addToHistory({
      type: 'drop',
      bananaAmount: actualDrop,
      beforeCarried,
      afterCarried: beforeCarried - actualDrop,
      beforeGround,
      afterGround: beforeGround + actualDrop,
    });
  };

  // 回退
  const undo = () => {
    if (history.length === 0 || gameState.isMoving) return;

    const lastStep = history[history.length - 1];
    
    if (lastStep.type === 'move') {
      setGameState((prev) => ({
        ...prev,
        position: lastStep.fromPosition,
        carriedBananas: lastStep.carriedBananas + lastStep.bananasUsed,
        isGameOver: false,
        gameOverReason: "",
        finalScore: 0,
      }));
    } else if (lastStep.type === 'pickup') {
      setGameState((prev) => ({
        ...prev,
        carriedBananas: lastStep.beforeCarried,
        isGameOver: false,
        gameOverReason: "",
      }));
      setGroundBananas((prev) => ({
        ...prev,
        [lastStep.position]: lastStep.beforeGround,
      }));
    } else if (lastStep.type === 'drop') {
      setGameState((prev) => ({
        ...prev,
        carriedBananas: lastStep.beforeCarried,
        isGameOver: false,
        gameOverReason: "",
      }));
      setGroundBananas((prev) => ({
        ...prev,
        [lastStep.position]: lastStep.beforeGround,
      }));
    }
    
    setHistory((prev) => prev.slice(0, -1));
  };

  // 重置游戏
  const resetGame = () => {
    setGameState({
      position: 0,
      carriedBananas: 0,
      isMoving: false,
      isGameOver: false,
      gameOverReason: "",
      finalScore: 0,
    });
    setGroundBananas({ 0: 300 });
    setHistory([]);
    setDropAmount("");
    // 注意：这里不重置 featureOptions，保持功能解锁状态
  };

  // 获取猴子状态描述和样式
  const getMonkeyStatusInfo = () => {
    if (gameState.isGameOver && !gameState.gameOverReason.includes("恭喜")) {
      // 异常状态
      return {
        text: gameState.gameOverReason,
        className:
          "text-center text-lg font-bold text-red-800 mb-3 p-4 bg-red-100 border-2 border-red-300 rounded-lg animate-pulse",
      };
    }
    if (
      gameState.gameOverReason.includes("恭喜") ||
      gameState.position === 100
    ) {
      // 成功状态
      const successText =
        gameState.position === 100
          ? `🏠🎉 恭喜！猴子成功到家了！带回了${gameState.carriedBananas}根香蕉！`
          : gameState.gameOverReason;
      return {
        text: successText,
        className:
          "text-center text-xl font-bold text-green-800 mb-3 p-4 bg-green-100 border-2 border-green-300 rounded-lg shadow-lg",
      };
    }
    if (gameState.isMoving) {
      return {
        text: "🏃‍♂️ 猴子正在努力前进中...",
        className: "text-center text-lg font-semibold text-blue-700 mb-3",
      };
    }
    if (gameState.position === 0) {
      return {
        text: "🌳 猴子在起点，准备开始搬香蕉之旅",
        className: "text-center text-lg font-semibold text-gray-700 mb-3",
      };
    }
    return {
      text: `🐵 猴子在${gameState.position}米处，背着${gameState.carriedBananas}根香蕉`,
      className: "text-center text-lg font-semibold text-gray-700 mb-3",
    };
  };

  // 渲染移动轨迹箭头
  const renderTrajectory = () => {
    // 过滤出移动操作
    const moveSteps = history.filter(step => step.type === 'move');
    const trajectoryHeight = Math.max(80, moveSteps.length * 30 + 40);
    const svgWidth = Math.max(600, windowWidth * 0.8);
    const pathWidth = svgWidth - 100;

    // 获取在指定位置的香蕉操作
    const getBananaOperationsAt = (position, beforeMoveIndex) => {
      const operations = [];
      // 查找在这个位置且在这次移动之前的香蕉操作
      for (let i = 0; i < history.length; i++) {
        const step = history[i];
        if (step.type !== 'move' && step.position === position) {
          // 找到这个香蕉操作对应的移动操作索引
          let moveIndex = 0;
          for (let j = 0; j <= i; j++) {
            if (history[j].type === 'move') moveIndex++;
          }
          if (moveIndex === beforeMoveIndex) {
            operations.push(step);
          }
        }
      }
      return operations;
    };

    return (
      <svg
        width="100%"
        height={trajectoryHeight}
        viewBox={`0 0 ${svgWidth} ${trajectoryHeight}`}
      >
        <defs>
          <marker
            id="arrowhead-traj"
            markerWidth="6"
            markerHeight="5"
            refX="5"
            refY="2.5"
            orient="auto"
          >
            <polygon points="0 0, 6 2.5, 0 5" fill="#10b981" />
          </marker>
          <marker
            id="arrowhead-left-traj"
            markerWidth="6"
            markerHeight="5"
            refX="5"
            refY="2.5"
            orient="auto"
          >
            <polygon points="6 2.5, 0 0, 0 5" fill="#f59e0b" />
          </marker>
        </defs>

        {moveSteps.map((step, index) => {
          const arrowY = 30 + index * 30;
          const startX = (step.fromPosition / 100) * pathWidth + 50;
          const endX = (step.toPosition / 100) * pathWidth + 50;
          const isRightward = endX > startX;
          const color = isRightward ? "#10b981" : "#f59e0b";
          const markerId = isRightward ? "arrowhead-traj" : "arrowhead-left-traj";

          // 获取起点和终点的香蕉操作
          const startOperations = getBananaOperationsAt(step.fromPosition, index + 1);
          const endOperations = getBananaOperationsAt(step.toPosition, index + 1);

          // 构建操作文本
          const getOperationText = (operations) => {
            if (!operations.length || !displayOptions.showBananaOperations) return '';
            return operations.map(op => {
              const symbol = op.type === 'pickup' ? '↑' : '↓';
              return `${symbol}${op.bananaAmount}`;
            }).join(',');
          };

          const startOpText = getOperationText(startOperations);
          const endOpText = getOperationText(endOperations);

          return (
            <g key={step.id}>
              <line
                x1={startX}
                y1={arrowY}
                x2={endX}
                y2={arrowY}
                stroke={color}
                strokeWidth="3"
                markerEnd={`url(#${markerId})`}
              />
              {displayOptions.showTrajectorySteps && (
                <text
                  x={(startX + endX) / 2}
                  y={arrowY - 8}
                  textAnchor="middle"
                  fontSize="12"
                  fill={color}
                  fontWeight="bold"
                >
                  {step.distance}m(-{step.bananasUsed}🍌)
                </text>
              )}
              {displayOptions.showRemainingBananas && (
                <text
                  x={isRightward ? endX + 15 : endX - 15}
                  y={arrowY + 4}
                  fontSize="11"
                  fill="#666"
                  textAnchor={isRightward ? "start" : "end"}
                  fontWeight="bold"
                >
                  剩{step.carriedBananas}🍌{endOpText && ` [${endOpText}]`}
                </text>
              )}
              {/* 起点操作信息 */}
              {startOpText && displayOptions.showBananaOperations && (
                <text
                  x={isRightward ? startX - 15 : startX + 15}
                  y={arrowY + 4}
                  fontSize="10"
                  fill="#8b5cf6"
                  textAnchor={isRightward ? "end" : "start"}
                  fontWeight="bold"
                >
                  [{startOpText}]
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  // 渲染基站
  const renderStations = () => {
    const stations = [];
    const svgWidth = Math.max(600, windowWidth * 0.8);
    const pathWidth = svgWidth - 100;

    Object.keys(groundBananas).forEach((position) => {
      const pos = parseInt(position);
      const bananas = groundBananas[pos];
      if (bananas > 0) {
        const x = 50 + (pos / 100) * pathWidth;
        stations.push(
          <g key={pos}>
            {/* 基站香蕉数 - 大字体突出显示 */}
            <text
              x={x}
              y="120"
              textAnchor="middle"
              fontSize="18"
              fill="#8b5cf6"
              fontWeight="bold"
              className="drop-shadow-sm"
            >
              📦{bananas}🍌
            </text>
            {displayOptions.showStationPositions && (
              <text
                x={x}
                y="138"
                textAnchor="middle"
                fontSize="8"
                fill="#6b7280"
                fontWeight="normal"
              >
                {pos}m
              </text>
            )}
          </g>
        );
      }
    });
    return stations;
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-6 bg-gradient-to-b from-sky-100 to-green-100 rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-green-800">
        🐵🍌 猴子搬香蕉探索工具
      </h1>

      {/* 状态显示 */}
      <div className="bg-white rounded-lg p-4 mb-6 shadow-md">
        <div className={getMonkeyStatusInfo().className}>
          {getMonkeyStatusInfo().text}
        </div>
        <div className="flex justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <span className="font-semibold">位置:</span>
            <span className="bg-blue-100 px-2 py-1 rounded">
              {gameState.position}米
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-semibold">背负:</span>
            <span className="bg-yellow-100 px-2 py-1 rounded font-bold text-lg">
              {gameState.carriedBananas}/100根
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-semibold">地面:</span>
            <span className="bg-green-100 px-2 py-1 rounded font-bold text-lg">
              {groundBananas[gameState.position] || 0}根
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-semibold">步数:</span>
            <span className="bg-purple-100 px-2 py-1 rounded">
              {history.length}
            </span>
          </div>
          {gameState.finalScore > 0 && (
            <div className="flex items-center space-x-2">
              <Home size={16} className="text-green-600" />
              <span className="font-semibold text-green-600">到家:</span>
              <span className="bg-green-200 px-2 py-1 rounded font-bold">
                {gameState.finalScore}根
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 可视化区域 */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-md">
        <svg
          width="100%"
          height="150"
          viewBox={`0 0 ${Math.max(600, windowWidth * 0.8)} 150`}
        >
          {(() => {
            const svgWidth = Math.max(600, windowWidth * 0.8);
            const pathWidth = svgWidth - 100;

            return (
              <>
                {/* 背景路径 */}
                <rect
                  x="50"
                  y="60"
                  width={pathWidth}
                  height="20"
                  fill="#8b5cf6"
                  opacity="0.3"
                  rx="10"
                />

                {/* 距离标记 - 只在开启时显示，且字体变小 */}
                {displayOptions.showDistanceMarkers && [0, 25, 50, 75, 100].map((distance) => (
                  <g key={distance}>
                    <line
                      x1={50 + (distance / 100) * pathWidth}
                      y1="55"
                      x2={50 + (distance / 100) * pathWidth}
                      y2="85"
                      stroke="#6b7280"
                      strokeWidth="1"
                      opacity="0.5"
                    />
                    <text
                      x={50 + (distance / 100) * pathWidth}
                      y="100"
                      textAnchor="middle"
                      fontSize="8"
                      fill="#9ca3af"
                      fontWeight="normal"
                    >
                      {distance}m
                    </text>
                  </g>
                ))}

                {/* 当前位置垂直指示线 */}
                {displayOptions.showPositionLine && (
                  <line
                    x1={50 + (gameState.position / 100) * pathWidth}
                    y1="35"
                    x2={50 + (gameState.position / 100) * pathWidth}
                    y2="145"
                    stroke="#6366f1"
                    strokeWidth="3"
                    strokeDasharray="5,5"
                    opacity="0.8"
                  />
                )}

                {/* 基站显示 */}
                {renderStations()}

                {/* 起点标记 */}
                <text
                  x="50"
                  y="20"
                  textAnchor="middle"
                  fontSize="14"
                  fill="#dc2626"
                  fontWeight="bold"
                >
                  🌳起点基站
                </text>

                {/* 终点标记 */}
                <text
                  x={50 + pathWidth}
                  y="20"
                  textAnchor="middle"
                  fontSize="14"
                  fill="#059669"
                  fontWeight="bold"
                >
                  🏠家
                </text>

                {/* 猴子 */}
                <g
                  transform={`translate(${
                    50 + (gameState.position / 100) * pathWidth
                  }, 70)`}
                >
                  <text
                    x="0"
                    y="0"
                    textAnchor="middle"
                    fontSize="28"
                    className={gameState.isMoving ? "animate-bounce" : ""}
                  >
                    {gameState.isGameOver &&
                    !gameState.gameOverReason.includes("恭喜")
                      ? "😵"
                      : gameState.carriedBananas > 30
                      ? "🐵"
                      : "🐒"}
                  </text>
                  {gameState.carriedBananas > 0 && (
                    <text
                      x="0"
                      y="-28"
                      textAnchor="middle"
                      fontSize="16"
                      fill="#b45309"
                      fontWeight="bold"
                      className="drop-shadow-sm"
                    >
                      🍌×{gameState.carriedBananas}
                    </text>
                  )}
                </g>
              </>
            );
          })()}
        </svg>
      </div>

      {/* 移动轨迹 */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg p-4 mb-4 shadow-md">
          <h3 className="text-lg font-semibold mb-2 text-gray-700">操作轨迹</h3>
          {renderTrajectory()}
        </div>
      )}

      {/* 控制面板 */}
      <div className="bg-white rounded-lg p-4 shadow-md">
        <div className="flex flex-wrap justify-between gap-4 mb-4">
          {/* 香蕉管理 */}
          <div className="min-w-[200px] flex-1 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              拿起香蕉
            </label>
            <div className="flex flex-wrap gap-1">
              <input
                type="number"
                value={targetCarriedBananas}
                placeholder="50"
                onChange={(e) => setTargetCarriedBananas(e.target.value)}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm placeholder-gray-400"
                min="0"
                max="100"
                disabled={gameState.isMoving || gameState.position === 100}
              />
              <button
                onClick={pickupBananas}
                disabled={gameState.isMoving || gameState.position === 100}
                className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 disabled:opacity-50"
              >
                拿起
              </button>
            </div>
          </div>

          {/* 放下香蕉 */}
          {featureOptions.enableDropBananas && (
            <div className="min-w-[200px] flex-1 space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                放下香蕉
              </label>
              <div className="flex flex-wrap gap-1">
                <input
                  type="number"
                  value={dropAmount}
                  placeholder="10"
                  onChange={(e) => setDropAmount(e.target.value)}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm placeholder-gray-400"
                  min="0"
                  max={gameState.carriedBananas}
                  disabled={gameState.isMoving || gameState.position === 100}
                />
                <button
                  onClick={() => dropBananas(parseInt(dropAmount) || 10)}
                  disabled={
                    gameState.isMoving ||
                    gameState.carriedBananas === 0 ||
                    gameState.position === 100
                  }
                  className="px-2 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:opacity-50"
                >
                  放下
                </button>
                <button
                  onClick={() => dropBananas(gameState.carriedBananas)}
                  disabled={
                    gameState.isMoving ||
                    gameState.carriedBananas === 0 ||
                    gameState.position === 100
                  }
                  className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
                >
                  全放
                </button>
              </div>
            </div>
          )}

          {/* 移动控制 */}
          <div className="min-w-[180px] flex-1 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              移动距离
            </label>
            <div className="flex flex-wrap gap-1">
              <input
                type="number"
                value={moveDistance}
                placeholder="10"
                onChange={(e) => setMoveDistance(e.target.value)}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm placeholder-gray-400"
                min="1"
                disabled={gameState.isMoving || gameState.position === 100}
              />
              <button
                onClick={() => moveMonkey("left")}
                disabled={
                  gameState.isMoving ||
                  gameState.position === 0 ||
                  gameState.position === 100 ||
                  (gameState.isGameOver &&
                    !gameState.gameOverReason.includes("恭喜"))
                }
                className="px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50"
              >
                <ArrowLeft size={14} />
              </button>
              <button
                onClick={() => moveMonkey("right")}
                disabled={
                  gameState.isMoving ||
                  gameState.position === 100 ||
                  (gameState.isGameOver &&
                    !gameState.gameOverReason.includes("恭喜"))
                }
                className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
              >
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* 游戏控制 */}
          <div className="min-w-[160px] flex-1 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              游戏控制
            </label>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={undo}
                disabled={
                  gameState.isMoving ||
                  history.length === 0 ||
                  gameState.position === 100
                }
                className="px-2 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center"
              >
                <RotateCcw size={12} className="mr-1" />
                撤销
              </button>
              <button
                onClick={resetGame}
                disabled={gameState.isMoving}
                className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
              >
                重置
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 显示设置 */}
      <div className="bg-white rounded-lg p-4 shadow-md mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-gray-800">🎨 显示设置</h4>
          <div className="flex gap-2">
            <button
              onClick={() => setDisplayOptions({
                showStationPositions: false,
                showPositionLine: false,
                showTrajectorySteps: false,
                showRemainingBananas: false,
                showBananaOperations: false,
                showDistanceMarkers: false,
              })}
              className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
            >
              简洁模式
            </button>
            <button
              onClick={() => setDisplayOptions({
                showStationPositions: true,
                showPositionLine: true,
                showTrajectorySteps: true,
                showRemainingBananas: true,
                showBananaOperations: true,
                showDistanceMarkers: true,
              })}
              className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
            >
              详细模式
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showStationPositions}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showStationPositions: e.target.checked
              }))}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">基站位置</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showPositionLine}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showPositionLine: e.target.checked
              }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">位置辅助线</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showTrajectorySteps}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showTrajectorySteps: e.target.checked
              }))}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">轨迹步数</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showRemainingBananas}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showRemainingBananas: e.target.checked
              }))}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700">剩余香蕉</span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showBananaOperations}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showBananaOperations: e.target.checked
              }))}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-700">香蕉操作</span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showDistanceMarkers}
              onChange={(e) => setDisplayOptions(prev => ({
                ...prev,
                showDistanceMarkers: e.target.checked
              }))}
              className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
            />
            <span className="text-sm text-gray-700">距离标记</span>
          </label>
        </div>
        
        {/* 高级功能解锁 */}
        <div className="border-t pt-3">
          <h5 className="font-semibold text-gray-700 mb-2">🔓 高级功能</h5>
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={featureOptions.enableDropBananas}
                onChange={(e) => setFeatureOptions(prev => ({
                  ...prev,
                  enableDropBananas: e.target.checked
                }))}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">解锁功能</span>
            </label>
            {!featureOptions.enableDropBananas && (
              <span className="text-xs text-gray-500 italic">💡 思考：如何才能带香蕉回家？</span>
            )}
          </div>
        </div>
      </div>

      {/* 游戏提示 */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
        <h4 className="font-semibold text-blue-800 mb-2">🎮 游戏规则</h4>
        <div className="text-sm text-blue-700 space-y-1">
          <p>• 小猴子摘了300根香蕉，起点离家100米</p>
          <p>• 每走1米消耗1根香蕉补充体力</p>
          <p>• 每次最多背100根香蕉，超重会被压倒</p>
          <p>• 探索最优策略，看看最多能搬多少香蕉回家！</p>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MonkeyBananaExplorer;
